import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';

const FAQs = () => {
  const [openId, setOpenId] = useState(null);

  const toggleFAQ = (id) => {
    setOpenId(openId === id ? null : id);
  };

  const faqData = [
    {
      id: 1,
      question: "What exactly does Validate Systems do?",
      answer: [
        "We provide two complementary services:",
        "Verification (human-verified checks): identity/background, employment & education confirmation, address verification (field visits), guarantor & corporate due diligence, and financial standing evidence (bank letters, income/asset attestations)—all consent-based and performed by trained case managers and vetted field agents.",
        "Travel & Immigration (advisory + documentation support): eligibility/route guidance, personalized document checklists & templates, background certificates, application assembly & admin quality control, appointment support, and mock interviews.",
        "We do not provide legal representation or guarantee outcomes."
      ]
    },
    {
      id: 2,
      question: "Which countries do you serve?",
      answer: [
        "Core on-the-ground coverage: Nigeria, Kenya, Ghana, Zimbabwe, and South Africa.",
        "We can support other countries via partners (feasibility and timelines vary). Tell us your route/country and we'll confirm what's realistic at intake."
      ]
    },
    {
      id: 3,
      question: "What are your typical timelines?",
      answer: [
        "Verification: Standard 3–7 business days (institution-dependent). Rush 24–72 hours where partners cooperate.",
        "Travel & Immigration (admin side):",
        "• Personalized checklist/plan: 1–2 business days after your advisory call.",
        "• Background certificates: 3–10 business days (institution-dependent).",
        "• File assembly & admin QA: 2–5 business days once all documents are ready.",
        "Embassy/consulate processing times are outside our control."
      ]
    },
    {
      id: 4,
      question: "How do you price your services?",
      answer: [
        "Verification: per-check pricing, with bundle rates for multi-check cases; Rush is optional.",
        "Travel & Immigration: base fee by route/destination, with optional add-ons (e.g., translations/notarization coordination, concierge appointment support, recorded mock interviews, evidence sourcing via Verification).",
        "Institutional clients: retainers and volume pricing available.",
        "You'll receive a clear quote before we start."
      ]
    },
    {
      id: 5,
      question: "Do you guarantee results or visa approvals?",
      answer: [
        "No. We guarantee a defensible, evidence-based process and clear reporting. Approvals and registrar/embassy responses are outside our control. If sources don't cooperate, we report Inconclusive with next-step options."
      ]
    },
    {
      id: 6,
      question: "What hours do you operate and how do I reach you?",
      answer: [
        "Mon–Fri, 09:00–17:00 WAT (Africa/Lagos). Reach us via the Contact page (email and WhatsApp available there). We reply to urgent/Rush requests as soon as feasible within business hours."
      ]
    },
    {
      id: 7,
      question: "What types of verification do you offer?",
      answer: [
        "Identity & Background: ID inspection, selfie+liveness (human), guarantor/referee calls, manual adverse-media/PEP/sanctions screening.",
        "Employment & Education: direct confirmations with HR desks/registrars (titles, dates, awards).",
        "Address Verification: in-person visit, neighbor/landlord corroboration, photos, geo/time stamps (where permitted).",
        "Financial Standing (consent-based): bank reference letters, proof-of-income, asset evidence.",
        "Corporate & Guarantor Due Diligence: company registry lookups, director/ownership confirmation (where records exist), guarantor identity/capacity checks.",
        "Don't see your need? We'll scope a bespoke check."
      ]
    },
    {
      id: 8,
      question: "How does your verification process work?",
      answer: [
        "Intake & scope, goal, country, documents, SLA preference.",
        "Human outreach & fieldwork, we contact source-of-truth institutions; where needed, we dispatch a field agent. We aim for two independent corroborations per claim.",
        "Dual review & supervisor sign-off, quality controls on every case.",
        "Signed report, tamper-evident PDF with sources, methods, timestamps, and artifacts. Outcome: Verified / Inconclusive / Discrepant."
      ]
    },
    {
      id: 9,
      question: "What does a verification report include?",
      answer: [
        "• Case ID, subject initials, requested checks, SLA",
        "• Evidence table: source, method (call/email/visit), date/time, summary, artifacts (photos, email headers, call log snippets)",
        "• Field pack (for addresses): photos, coordinates, timestamps, notes",
        "• Quality controls: reviewer names & sign-offs",
        "• Tamper control: document hash & QR for authenticity check"
      ]
    },
    {
      id: 10,
      question: "Will you contact a current employer or landlord?",
      answer: [
        "Only with explicit, written consent and an agreed approach to avoid jeopardizing relationships. We can time outreach or limit who we speak to."
      ]
    },
    {
      id: 11,
      question: "What happens if information conflicts or a source won't respond?",
      answer: [
        "We escalate to obtain a second corroboration. If the conflict persists or sources remain unresponsive, we mark Discrepant or Inconclusive with reasons and recommended next steps."
      ]
    },
    {
      id: 12,
      question: "Can you work outside your five core countries?",
      answer: [
        "Yes—via partners. We'll confirm feasibility, costs, and timeline windows before you approve."
      ]
    },
    {
      id: 13,
      question: "How do you ensure privacy and consent?",
      answer: [
        "We are consent-first. We only proceed with proper permissions, store evidence securely, and operate under defined retention timelines (shared at intake). We never sell or share data for marketing."
      ]
    },
    {
      id: 14,
      question: "Can I get a sample verification report?",
      answer: [
        "Yes—request a redacted sample via Contact. We'll send a PDF with real-world structure and artifacts (personal data removed)."
      ]
    },
    {
      id: 15,
      question: "Are you lawyers or regulated immigration representatives?",
      answer: [
        "No. We provide administrative/documentation support: route guidance, checklists, templates, evidence coordination, file assembly, and mock interviews. We don't give legal advice or represent you before authorities."
      ]
    },
    {
      id: 16,
      question: "What exactly do you do for visa/immigration files?",
      answer: [
        "• Eligibility & route guidance (study, work, visit, business, family)",
        "• Personalized checklists & templates (sponsor, employer, invitation letters; file-naming rules)",
        "• Background certificates via our Verification team (consent-based)",
        "• Application assembly & admin QA (ordering, cross-references, criteria mapping)",
        "• Appointment support & mock interviews (recorded, with notes)",
        "• Post-decision admin support (gap analysis for refusals; travel-readiness checklists for approvals)"
      ]
    },
    {
      id: 17,
      question: "Who submits my application?",
      answer: [
        "You do (or your chosen visa center/portal). We prepare, structure, and quality-check; we can guide submission step-by-step but do not submit on your behalf."
      ]
    },
    {
      id: 18,
      question: "Can you help after a refusal?",
      answer: [
        "Yes. We perform a documentation gap analysis, identify weak/missing evidence, and design a stronger, sequenced plan for a future attempt. We don't dispute decisions or provide legal representation."
      ]
    },
    {
      id: 19,
      question: "Do you offer translations, notarization, or courier services?",
      answer: [
        "We coordinate certified translations/notarizations and organize courier/logistics as an add-on (vendor fees are pass-through; we handle QC against embassy specs). We don't provide legal services ourselves."
      ]
    },
    {
      id: 20,
      question: "What are your premium/concierge options?",
      answer: [
        "Pro Assembly: full file build, compliance pack, appointment support, one mock interview.",
        "Concierge: adds evidence sourcing (via Verification), ADR handling (up to a defined number of rounds), priority/Rush windows, and a named case manager.",
        "Ask for our current packages & add-ons list."
      ]
    },
    {
      id: 21,
      question: "How do you prevent fraud and protect integrity?",
      answer: [
        "• Human-led checks with auditable trails; no bots/scraping.",
        "• Chain-of-custody logs for sensitive files; restricted access; tamper-evident report hashes.",
        "• Where allowed, geo/time-stamped field evidence for address checks.",
        "• Dual review on all verification cases."
      ]
    },
    {
      id: 22,
      question: "How long do you keep my data? Can I request deletion?",
      answer: [
        "We keep data only as long as necessary to complete work, meet regulatory/contractual needs, and handle reasonable after-care. Retention periods are disclosed at intake. You may request deletion subject to legal/contractual constraints."
      ]
    }
  ];

  return (
    <section className="py-16 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="text-left mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack">
            Frequently Asked Questions
          </h2>
        </motion.div>

        {/* FAQ Items */}
        <div className="max-w-4xl">
          {faqData.map((faq, index) => (
            <motion.div 
              key={faq.id} 
              className="border-b border-gray-300 py-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <button 
                className="w-full text-left flex justify-between items-center font-semibold text-lg text-acBlack focus:outline-none hover:text-acYellow transition-colors" 
                onClick={() => toggleFAQ(faq.id)}
              >
                {faq.question}
                <ChevronDown size={20} className={`transition-transform flex-shrink-0 ml-4 ${openId === faq.id ? 'rotate-180' : ''}`} />
              </button>

              <motion.div
                className="overflow-hidden"
                initial={{ height: 0, opacity: 0 }}
                animate={openId === faq.id ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="text-gray-600 mt-4 text-sm leading-relaxed">
                  {faq.answer.map((paragraph, index) => (
                    <p key={index} className="mb-3 last:mb-0">{paragraph}</p>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQs;
